import { ChevronLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import LightningDocs from 'pages/Loan/LightningDocs'
import { Fragment, useEffect, useState } from 'react'
import { getGenerateDocuments, getInvestorServicersGetServicers, updateGenerateDocuments } from 'services'
import { Button } from 'stories/components'
import { InputConvert, InputValidate, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs, loanSaleKeys, lockoutKeys, prepaymentKeys } from './constant'

export default function LightningDocsClosingScreen() {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [inputFieldChanged, setInputFieldChanged] = useState(false)
  const [showLightningDocs, setShowLightningDocs] = useState(false)
  const [akaEntries, setAkaEntries] = useState<Array<{ name: string; aka: string[] }>>([])
  const [akaChanged, setAkaChanged] = useState(false)

  const initData = async () => {
    setLoanNumber()
    setLoading(true)
    const [res, { data }] = await Promise.all([getInvestorServicersGetServicers(), getGenerateDocuments(true)])
    let newInputs = defaultInputs()
    newInputs.servicer.options = [...newInputs.servicer.options, ...res.map((item: any) => item.investorCode)]
    if (!data) {
      setInputs(newInputs)
      setLoading(false)
      return
    }
    Object.keys(data).forEach((key) => {
      if (newInputs[key] && data[key] !== undefined) newInputs[key].value = data[key]
    })
    if (Array.isArray(data.akaInformation)) {
      setAkaEntries(data.akaInformation)
    }
    setInputs(newInputs)
    setLoading(false)
  }
  useEffect(() => {
    initData()
  }, [])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    setInputFieldChanged(true)
  }

  const showHistory = (key: string) => {
    const options = {
      table: 'GenerateDocument',
      field: key,
      keys: {
        field: key,
      },
    }
    openAuditLog(options)
  }

  const onBlur = (key: string) => {
    if (!inputFieldChanged) return
    setInputFieldChanged(false)

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}

    data[key] = newInputs[key].value
    newInputs[key].error = InputValidate(newInputs[key])
    if (newInputs[key].error) return

    setLoading(true)

    updateGenerateDocuments(data)
      .then(() => {})
      .finally(() => setLoading(false))
  }

  const addAkaEntry = () => {
    const newEntries = [...akaEntries, { name: '', aka: [] }]
    setAkaEntries(newEntries)
    saveAkaInformation(newEntries)
  }

  const removeAkaEntry = (index: number) => {
    const newEntries = akaEntries.filter((_, i) => i !== index)
    setAkaEntries(newEntries)
    saveAkaInformation(newEntries)
  }

  const onAkaFieldChange = (entryIndex: number, field: 'name' | 'aka', value: any) => {
    const newEntries = cloneDeep(akaEntries)
    newEntries[entryIndex][field] = value
    setAkaEntries(newEntries)
    if (field === 'aka') {
      saveAkaInformation(newEntries)
    } else {
      setAkaChanged(true)
    }
  }

  const onAkaBlur = () => {
    if (!akaChanged) return
    setAkaChanged(false)
    saveAkaInformation()
  }

  const saveAkaInformation = (entries?: Array<{ name: string; aka: string[] }>) => {
    setLoading(true)
    updateGenerateDocuments({ akaInformation: entries || akaEntries })
      .then(() => {})
      .finally(() => setLoading(false))
  }

  if (showLightningDocs) {
    return (
      <div className="GenerateDocument-container relative">
        <h2 className="text-2xl font-bold flex items-center mb-3">Lightning Docs</h2>
        <div className="text-shade-blue flex items-center mb-2 font-bold">
          <ChevronLeftIcon className="w-4 h-4"></ChevronLeftIcon>
          <span className="hover:underline cursor-pointer" onClick={() => setShowLightningDocs(false)}>
            Back
          </span>
        </div>
        <LightningDocs hasOverview={false} />
      </div>
    )
  }
  return (
    <>
      <div className="GenerateDocument-container relative">
        <LayoutLoading show={isLoading} />
        <h2 className="text-2xl font-bold flex items-center mb-3">Lightning Docs</h2>
        <div className="grid gap-4 md:grid-cols-12 grid-cols-1">
          <div className="md:col-span-12">
            <div className="border-b font-semibold mb-4">Details</div>
            <div className="w-full overflow-auto rounded">
              <div className="w-full grid gap-4 md:grid-cols-2 grid-cols-1">
                {Object.keys(inputs).map((key, index) => {
                  let input = inputs[key]
                  input.history = !input.disabled && key != 'interestType'

                  if (['numberOfExtensions', 'extensionFeePerc', 'IncrementOfExtensions'].includes(key)) {
                    if (!inputs?.enableExtension?.value) input.disabled = true
                    else input.disabled = false
                  }

                  if (inputs?.servicer?.value !== 'Other' && ['servicerName', 'servicerAddress'].includes(key)) return
                  if (!inputs?.isLoanSale?.value && loanSaleKeys.includes(key)) return
                  if (
                    inputs?.loanSaleAssignee?.value !== 'Other' &&
                    ['loanSaleAssigneeName', 'loanSaleAssigneeAddress'].includes(key)
                  )
                    return
                  if (!inputs?.isPrepaymentPremium?.value && prepaymentKeys.includes(key)) return
                  if (!inputs?.prepaymentPremium?.value && prepaymentKeys.includes(key) && key !== 'prepaymentPremium')
                    return

                  let termKey = ''
                  switch (inputs?.prepaymentPremium?.value) {
                    case 'Guaranteed Interest':
                      termKey = 'prepaymentTerm0'
                      break
                    case 'Linear Step':
                      termKey = 'prepaymentTerm1'
                      break
                    case 'Lockout':
                      termKey = 'prepaymentTerm2'
                      break
                    case 'Non Linear Step':
                      termKey = 'prepaymentTerm3'
                      break
                  }
                  if (key.includes('prepaymentTerm') && key !== termKey) return

                  if (inputs?.prepaymentPremium?.value !== 'Lockout' && lockoutKeys.includes(key)) return
                  if (inputs?.prepaymentPremium?.value !== 'Non Linear Step' && key === 'prepaymentSteps') return

                  let calculationFromKey = ''
                  switch (inputs?.prepaymentLockoutCalculation?.value) {
                    case 'Dollar Amount':
                      calculationFromKey = 'prepaymentLockoutAmount'
                      break
                    case 'Months':
                      calculationFromKey = 'prepaymentLockoutMonths'
                      break
                    case 'Prepaid':
                      calculationFromKey = 'prepaymentLockoutPrepaid'
                      break
                  }

                  if (
                    ['prepaymentLockoutAmount', 'prepaymentLockoutMonths', 'prepaymentLockoutPrepaid'].includes(key) &&
                    key !== calculationFromKey
                  )
                    return

                  return (
                    <Fragment key={index}>
                      <div className={`input md:col-span-${input.span || 1}`}>
                        <RenderInput
                          input={input}
                          Key={key}
                          onChange={onChange}
                          onBlur={onBlur}
                          showHistory={showHistory}
                        />
                      </div>
                      {key === 'isAKA' && !!inputs?.isAKA?.value && (
                        <div className="md:col-span-2 border rounded p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <p className="font-semibold text-sm">Enter all individuals who require an AKA</p>
                            <button
                              className="flex items-center gap-1 text-shade-blue hover:underline text-sm font-semibold"
                              onClick={addAkaEntry}
                            >
                              <PlusIcon className="w-4 h-4" />
                              Add Individual
                            </button>
                          </div>
                          {akaEntries.map((entry, entryIndex) => (
                            <div key={entryIndex} className="border rounded p-3 mb-3 bg-white relative">
                              <div className="absolute top-2 right-2">
                                <button
                                  className="text-red-500 hover:text-red-700 p-1"
                                  onClick={() => removeAkaEntry(entryIndex)}
                                  title="Remove"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="grid gap-4 md:grid-cols-2 grid-cols-1 pr-8">
                                <div className="md:col-span-1">
                                  <RenderInput
                                    input={{
                                      inputType: 'text',
                                      title: 'Select Individual [Name for Signature Affidavit]',
                                      value: entry.name,
                                    }}
                                    Key={`akaName-${entryIndex}`}
                                    onChange={(_: string, value: string) => onAkaFieldChange(entryIndex, 'name', value)}
                                    onBlur={onAkaBlur}
                                  />
                                </div>
                                <div className="md:col-span-1">
                                  <RenderInput
                                    input={{
                                      inputType: 'autocomplete',
                                      title: 'Enter all AKAs',
                                      value: entry.aka,
                                      type: 'text',
                                      checkExisting: false,
                                    }}
                                    Key={`akaAka-${entryIndex}`}
                                    onChange={(_: string, value: any) => onAkaFieldChange(entryIndex, 'aka', value)}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          {akaEntries.length === 0 && (
                            <p className="text-gray-500 text-sm italic">No individuals added yet.</p>
                          )}
                        </div>
                      )}
                    </Fragment>
                  )
                })}
              </div>
            </div>
          </div>
          <div className="col-span-full text-center">
            <Button onClick={() => setShowLightningDocs(true)}>Proceed to generate Lightning Docs</Button>
          </div>
        </div>
      </div>
    </>
  )
}
